import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@systemeio/components';
import { CurrencyEnum } from '@systemeio/types';
import { cents, centsToUnits } from '@systemeio/util';
import { ChartData } from 'chart.js';
import { AffiliateCharInterface, AffiliateStatisticCommonInterface } from 'modules/affiliate-program/affiliate/types/affiliate-interface';
import { getAffiliateChartColors, getAffiliateChartTextColor } from 'modules/affiliate-program/affiliate/utils/get-affiliate-chart-color';
import { getAffiliateChartOptions } from 'modules/affiliate-program/affiliate/utils/get-affiliate-chart-options';
import { getChartValue } from 'modules/affiliate-program/affiliate/utils/get-chart-value';
import { getSortedChartCurrencies } from 'modules/affiliate-program/affiliate/utils/get-sorted-chart-currencies';
import { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { ChartCard } from 'shared/components/chart/chart-card';
import { CurrencyNameEnum } from 'shared/enums/currency-enum';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { currencyFormat } from 'shared/utils/currency-format';
import { toFullDate, toShortDate } from 'shared/utils/date-time-format';
interface AffiliateChartProps {
  chartData?: AffiliateCharInterface;
  selectedCurrency?: CurrencyEnum;
  affiliateStatistic?: AffiliateStatisticCommonInterface;
}
const AffiliateChart = t0 => {
  const $ = _c(40);
  const {
    chartData,
    selectedCurrency,
    affiliateStatistic
  } = t0;
  const {
    user,
    intlLocale
  } = useUser();
  const {
    t
  } = useLocoTranslation();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      labels: [],
      datasets: []
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [data, setData] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [allCurrencies, setAllCurrencies] = useState(t2);
  let t3;
  let t4;
  if ($[2] !== affiliateStatistic) {
    t3 = () => {
      if (affiliateStatistic) {
        setAllCurrencies(Object.keys(affiliateStatistic.total) as CurrencyEnum[]);
      }
    };
    t4 = [affiliateStatistic];
    $[2] = affiliateStatistic;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const [currencies, setCurrencies] = useState(t5);
  let t6;
  let t7;
  if ($[6] !== affiliateStatistic || $[7] !== allCurrencies || $[8] !== selectedCurrency) {
    t6 = () => {
      if (!affiliateStatistic) {
        return;
      }
      if (selectedCurrency) {
        setCurrencies([selectedCurrency]);
      } else {
        setCurrencies(allCurrencies);
      }
    };
    t7 = [affiliateStatistic, allCurrencies, selectedCurrency];
    $[6] = affiliateStatistic;
    $[7] = allCurrencies;
    $[8] = selectedCurrency;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] !== chartData || $[12] !== currencies) {
    t8 = chartData ? getSortedChartCurrencies([...currencies], chartData.sums) : currencies;
    $[11] = chartData;
    $[12] = currencies;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const sortedCurrencies = t8;
  let t10;
  let t9;
  if ($[14] !== allCurrencies || $[15] !== chartData || $[16] !== intlLocale || $[17] !== sortedCurrencies || $[18] !== t) {
    t9 = () => {
      if (chartData) {
        setData({
          labels: Object.keys(chartData.days).map(el => toShortDate(el, intlLocale)),
          datasets: sortedCurrencies.map(cur => {
            const {
              fill,
              hover
            } = getAffiliateChartColors(cur, allCurrencies);
            return {
              data: Object.keys(chartData.days).map(el_0 => getChartValue(chartData.days[el_0]?.[cur] ?? cents(0)) / 100),
              label: t(CurrencyNameEnum[cur]),
              backgroundColor: fill,
              hoverBackgroundColor: hover
            };
          })
        });
      }
    };
    t10 = [chartData, sortedCurrencies, allCurrencies, t, intlLocale];
    $[14] = allCurrencies;
    $[15] = chartData;
    $[16] = intlLocale;
    $[17] = sortedCurrencies;
    $[18] = t;
    $[19] = t10;
    $[20] = t9;
  } else {
    t10 = $[19];
    t9 = $[20];
  }
  useEffect(t9, t10);
  let t11;
  if ($[21] !== t) {
    t11 = t("dashboard.affiliate.commission_earned");
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== affiliateStatistic || $[24] !== allCurrencies || $[25] !== chartData || $[26] !== sortedCurrencies || $[27] !== user?.dashboardLocale) {
    t12 = affiliateStatistic && chartData ? <ul className="flex flex-wrap gap-3">{sortedCurrencies.map(cur_0 => <li key={cur_0} style={getAffiliateChartTextColor(cur_0, allCurrencies)}>{currencyFormat(centsToUnits(getChartValue(chartData.sums[cur_0] ?? cents(0))), cur_0, user?.dashboardLocale, true)}</li>)}</ul> : <Skeleton className="rounded-lg px-5 text-transparent">.</Skeleton>;
    $[23] = affiliateStatistic;
    $[24] = allCurrencies;
    $[25] = chartData;
    $[26] = sortedCurrencies;
    $[27] = user?.dashboardLocale;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== chartData || $[30] !== data || $[31] !== intlLocale || $[32] !== selectedCurrency || $[33] !== sortedCurrencies || $[34] !== user?.dashboardLocale) {
    t13 = chartData ? <Bar options={getAffiliateChartOptions({
      currencies: sortedCurrencies,
      locale: user?.dashboardLocale,
      selectedCurrency,
      tooltipTitles: Object.keys(chartData.days).map(date => toFullDate(date, intlLocale))
    })} data={data} height={430} /> : <Skeleton className="h-[430px] rounded-lg text-transparent">.</Skeleton>;
    $[29] = chartData;
    $[30] = data;
    $[31] = intlLocale;
    $[32] = selectedCurrency;
    $[33] = sortedCurrencies;
    $[34] = user?.dashboardLocale;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t11 || $[37] !== t12 || $[38] !== t13) {
    t14 = <ChartCard className="overflow-x-hidden" title={t11} value={t12}>{t13}</ChartCard>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  return t14;
};
export default AffiliateChart;